import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

import RichTextEditor from "@/components/RichTextEditor";
const CheckList = ({ title, items, selected, onToggle, testid, empty }) => (
  <div>
    <label className="text-xs font-medium text-[#1A242B] mb-1 block">{title}</label>
    <div className="max-h-40 overflow-y-auto rounded-xl border border-[#E5E0D8] bg-white p-2 space-y-1" data-testid={testid}>
      {items.length === 0 && <p className="text-xs text-[#8FA89B] p-2">{empty}</p>}
      {items.map((o) => (
        <label key={o.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#144F75]/[0.04] text-sm text-[#4A5D6B] cursor-pointer" data-testid={`${testid}-${o.id}`}>
          <input type="checkbox" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} className="accent-[#144F75]" />
          <span className="flex-1">{o.label}</span>
        </label>
      ))}
    </div>
  </div>
);

const NewBroadcast = ({ pkgId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [b, setB] = useState({ title: "", description: "", date: "", start_time: "", duration_min: 60 });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enhancer/program-broadcasts", { ...b, duration_min: parseInt(b.duration_min) || 60, package_id: pkgId || "" });
      toast.success("Broadcast added to this program — only its group members can watch.");
      onCreated(data);
      setB({ title: "", description: "", date: "", start_time: "", duration_min: 60 });
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="eyw-btn-ghost !py-1.5 text-sm" data-testid="pkg-new-broadcast-open">+ Create a broadcast for this program</button>;
  return (
    <div className="rounded-xl border border-[#E5E0D8] bg-white p-3 space-y-2" data-testid="pkg-new-broadcast">
      <input value={b.title} onChange={up("title")} placeholder="Broadcast title" className="eyw-input w-full" data-testid="pkg-bc-title" />
      <textarea value={b.description} onChange={up("description")} rows={2} placeholder="What it's about" className="eyw-input w-full resize-none" data-testid="pkg-bc-desc" />
      <div className="grid grid-cols-3 gap-2">
        <input type="date" value={b.date} onChange={up("date")} className="eyw-input" data-testid="pkg-bc-date" />
        <input type="time" value={b.start_time} onChange={up("start_time")} className="eyw-input" data-testid="pkg-bc-time" />
        <input type="number" min="15" step="15" value={b.duration_min} onChange={up("duration_min")} className="eyw-input" title="Minutes" data-testid="pkg-bc-duration" />
      </div>
      <p className="text-[11px] text-[#8FA89B]">Private to this program's group — it won't show in public listings.</p>
      <div className="flex gap-2">
        <button type="button" disabled={busy || !b.title.trim() || !b.date || !b.start_time} onClick={save} className="eyw-btn !py-1.5 text-sm disabled:opacity-50" data-testid="pkg-bc-save">Add broadcast</button>
        <button type="button" onClick={() => setOpen(false)} className="eyw-btn-ghost !py-1.5 text-sm" data-testid="pkg-bc-cancel">Cancel</button>
      </div>
    </div>
  );
};

// Enhancer programs: center services at approved centers, hosted group calls/events/broadcasts, group size.
const ProgramOptions = ({ opts, form, toggle, set, pkgId, onBroadcast }) => (
  <div className="space-y-3 rounded-2xl border border-[#D98E73]/30 bg-[#D98E73]/[0.04] p-3" data-testid="pkg-program-options">
    <CheckList title="Center services at your approved centers" testid="pkg-center-svc" selected={form.eligible_service_ids}
      onToggle={(id) => toggle(id)} empty="No center services available at your approved centers yet."
      items={(opts?.center_services || []).map((c) => ({ id: c.id, label: `${c.title}${c.centers?.length ? ` · ${c.centers.join(", ")}` : ""}` }))} />
    <CheckList title="Group calls, events & broadcasts you host" testid="pkg-events" selected={form.included_event_ids}
      onToggle={(id) => toggle(id, "included_event_ids")} empty="Create a class, event or broadcast first to include it."
      items={(opts?.events || []).map((e) => ({ id: e.id, label: `${e.title} · ${e.date || "date TBD"}${e.is_broadcast ? " · broadcast" : ""}` }))} />
    <NewBroadcast pkgId={pkgId} onCreated={onBroadcast} />
    <div>
      <label className="text-xs text-[#4A5D6B]">Group size limit (0 = no limit)</label>
      <input type="number" min="0" value={form.group_capacity} onChange={set("group_capacity")} data-testid="pkg-group-capacity" className="eyw-input w-full" />
      <p className="text-[11px] text-[#8FA89B] mt-1">Every buyer joins this package's private group. When it's full, new buyers join the waitlist.</p>
    </div>
  </div>
);

// Reusable package builder for enhancers and location managers.
// Props: pkg (edit target or {}), servicesEndpoint, createEndpoint, updateBase
export default function PackageFormModal({ pkg, servicesEndpoint, createEndpoint, updateBase, onClose, onSaved }) {
  const editing = !!pkg?.id;
  const [form, setForm] = useState({
    name: pkg?.name || "", description: pkg?.description || "",
    sessions: pkg?.sessions ?? 4, price: pkg?.price ?? 0,
    session_value: pkg?.session_value ?? 75, validity_days: pkg?.validity_days ?? 365,
    eligible_service_ids: pkg?.eligible_service_ids || [],
    included_event_ids: pkg?.included_event_ids || [],
    group_capacity: pkg?.group_capacity ?? 0,
  });
  const isEnhancer = createEndpoint === "/enhancer/packages";
  const qc = useQueryClient();
  const { data: progOpts } = useQuery({
    queryKey: ["pkg-program-options"], enabled: isEnhancer,
    queryFn: () => api.get("/enhancer/package-options").then((r) => r.data),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const { data: services = [] } = useQuery({
    queryKey: ["pkg-service-options", servicesEndpoint],
    queryFn: () => api.get(servicesEndpoint).then((r) => r.data),
  });
  const opts = services.map((s) => ({ id: s.id, title: s.title }));

  const toggle = (id, key = "eligible_service_ids") => setForm((f) => ({
    ...f,
    [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.eligible_service_ids.length === 0) { setError("Pick at least one of your offerings this package covers."); return; }
    setLoading(true);
    const payload = {
      ...form,
      sessions: parseInt(form.sessions) || 0,
      price: parseFloat(form.price) || 0,
      session_value: parseFloat(form.session_value) || 0,
      validity_days: parseInt(form.validity_days) || 365,
      group_capacity: parseInt(form.group_capacity) || 0,
    };
    try {
      if (editing) await api.put(`${updateBase}/${pkg.id}`, payload);
      else await api.post(createEndpoint, payload);
      toast.success(editing ? "Package updated — it's live." : "Package created — it's live on your storefront.");
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="package-form-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-1">{editing ? "Edit package" : "New package"}</h3>
        <p className="text-xs text-[#4A5D6B] mb-5">Bundle your own sessions and sell them from your storefront. Goes live immediately.</p>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Package name (e.g. 4-session Reiki bundle)" value={form.name} onChange={set("name")} data-testid="pkg-name" className="eyw-input w-full" />
          <RichTextEditor value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} testid="pkg-desc" placeholder="What's included / who it's for" minHeight={110} enableBlocks={false} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-[#4A5D6B]"># Sessions</label><input type="number" min="1" value={form.sessions} onChange={set("sessions")} data-testid="pkg-sessions" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Price $</label><input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="pkg-price" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Session value $</label><input type="number" step="0.01" value={form.session_value} onChange={set("session_value")} data-testid="pkg-session-value" className="eyw-input w-full" /></div>
          </div>
          <div><label className="text-xs text-[#4A5D6B]">Valid for (days)</label><input type="number" value={form.validity_days} onChange={set("validity_days")} data-testid="pkg-validity" className="eyw-input w-full" /></div>
          <div>
            <label className="text-xs font-medium text-[#1A242B] mb-1 block">Sessions can be redeemed for your offerings:</label>
            <div className="max-h-44 overflow-y-auto rounded-xl border border-[#E5E0D8] bg-white p-2 space-y-1" data-testid="pkg-services">
              {opts.length === 0 && <p className="text-xs text-[#8FA89B] p-2">You have no offerings yet — create a service first, then it can be bundled here.</p>}
              {opts.map((o) => (
                <label key={o.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#144F75]/[0.04] text-sm text-[#4A5D6B] cursor-pointer" data-testid={`pkg-svc-${o.id}`}>
                  <input type="checkbox" checked={form.eligible_service_ids.includes(o.id)} onChange={() => toggle(o.id)} className="accent-[#144F75]" />
                  {o.title}
                </label>
              ))}
            </div>
          </div>
          {isEnhancer && <ProgramOptions opts={progOpts} form={form} toggle={toggle} set={set} pkgId={pkg?.id}
            onBroadcast={(ev) => { qc.setQueryData(["pkg-program-options"], (o) => ({ ...(o || {}), events: [ev, ...((o || {}).events || [])] })); setForm((f) => ({ ...f, included_event_ids: [...new Set([...f.included_event_ids, ev.id])] })); }} />}
          <p className="text-[11px] text-[#8FA89B]">Package sales are shared with the ecosystem (finder's fee applies to your net, same as your other offerings).</p>
          {error && <p className="text-sm text-red-600" data-testid="pkg-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="pkg-save" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {editing ? "Save changes" : "Create package"}
          </button>
        </form>
      </div>
    </div>
  );
}
